/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    PublicClientApplication as BrowserClientApplication,
    Configuration,
    BrowserPerformanceClient,
    AzureCloudInstance,
    IPerformanceClient,
    StubPerformanceClient,
    IPublicClientApplication,
    BrowserUtils,
} from "@azure/msal-browser";
import {
    TelemetryReporter,
    TelemetryReporterConfiguration,
} from "../telemetry/TelemetryReporter.js";
import {
    intFields,
    PerformanceEventAbbreviations,
} from "../telemetry/TelemetryUtils.js";

const telemetryAllowedClouds: string[] = [
    AzureCloudInstance.AzurePublic,
    AzureCloudInstance.AzurePpe,
];

/**
 * Returns boolean flag, indicating whether telemetry capturing is allowed for the authority
 * @param configuration
 */
function isTelemetryAllowedCloud(configuration: Configuration) {
    if (!configuration.auth.authority) {
        return true;
    }

    return telemetryAllowedClouds.some(
        (cloud) => configuration.auth.authority?.indexOf(cloud.toString()) === 0
    );
}

export class PublicClientApplication extends BrowserClientApplication {
    protected performanceClient: IPerformanceClient;
    protected channelId: string;

    constructor(
        configuration: Configuration,
        perfTelemetryConfiguration?: TelemetryReporterConfiguration
    ) {
        let performanceClient: IPerformanceClient;
        const isBrowserEnvironment = typeof window !== "undefined";
        const telemetryEnabled =
            !perfTelemetryConfiguration?.disabled &&
            isTelemetryAllowedCloud(configuration) &&
            isBrowserEnvironment;

        if (telemetryEnabled) {
            performanceClient =
                configuration.telemetry?.client ||
                new BrowserPerformanceClient(
                    configuration,
                    new Set(intFields),
                    new Map(PerformanceEventAbbreviations)
                );
            super({
                ...configuration,
                telemetry: {
                    ...configuration.telemetry,
                    client: performanceClient,
                },
            });
        } else {
            super(configuration);
            performanceClient = new StubPerformanceClient();
        }
        this.performanceClient = performanceClient;
        this.channelId = isBrowserEnvironment ? BrowserUtils.createGuid() : "";
        /*
         * TODO: Don't initialize wrapper until eSTS is updated to include new SKU
         * this.initializeWrapperLibrary(WrapperSKU.FirstParty, version);
         */
        if (telemetryEnabled) {
            const telemetryReporter = new TelemetryReporter(
                configuration.auth.clientId,
                this.getLogger(),
                performanceClient,
                this.channelId,
                this.getActiveAccount.bind(this),
                this.getAllAccounts.bind(this),
                perfTelemetryConfiguration
            );
            this.addPerformanceCallback(
                telemetryReporter.getTelemetryCallback()
            );
        }
    }

    public static async createPublicClientApplication(
        configuration: Configuration,
        perfTelemetryConfiguration?: TelemetryReporterConfiguration,
        correlationId?: string
    ): Promise<IPublicClientApplication> {
        const pca = new PublicClientApplication(
            configuration,
            perfTelemetryConfiguration
        );
        await pca.initialize({ correlationId });
        return pca;
    }
}
